import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { SessionExpiryService } from './session-expiry.service';
import { SessionStorageService } from '../storage/session-storage.service';

export const HEARTBEAT_EVENTS = {
  loginEvent: 'heartbeat/events/login',
  heartbeatEvent: 'heartbeat/events/heartbeat',
  logoutEvent: 'heartbeat/events/logout',
} as const;

export type HeartbeatEvent = keyof typeof HEARTBEAT_EVENTS;

@Injectable({ providedIn: 'root' })
export class HeartbeatService {
  private readonly injector = inject(Injector);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly expiry = inject(SessionExpiryService);
  private timer: ReturnType<typeof setInterval> | null = null;
  private userId = '';
  private rbsToken = '';

  get hasTimer(): boolean {
    return this.timer !== null;
  }

  start(userId: string, rbsToken: string): void {
    if (this.timer !== null) return;
    this.userId = userId;
    this.rbsToken = rbsToken;
    const firstEvent: HeartbeatEvent = this.sessionStorage.get(environment.heartbeatLoginEventKey)
      ? 'heartbeatEvent'
      : 'loginEvent';
    this.sendEvent(firstEvent).subscribe();
    this.sessionStorage.set(environment.heartbeatLoginEventKey, 'true');
    this.timer = setInterval(
      () => this.sendEvent('heartbeatEvent').subscribe(),
      environment.heartbeatTimeIntervalMs,
    );
  }

  destroy(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  sendEvent(event: HeartbeatEvent): Observable<unknown> {
    const http = this.injector.get(HttpClient);
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.rbsToken}`,
      userId: this.userId,
      'x-apiscope': 'realize',
    });
    const url = `${environment.heartbeatBaseUrl}/v2/${HEARTBEAT_EVENTS[event]}`;
    return http.post(url, '', { headers }).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          this.expiry.notifyExpired();
        }
        return of(null);
      }),
    );
  }
}
